/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Provides access to the brand-domain resource of the user-management service
 * (`/organizations/:orgId/domains`).
 *
 * Features:
 * - `load$()` — cached, deduplicated list of brand domains for an organisation
 * - `create$()`, `update$()`, `delete$()` — mutations that invalidate the list cache
 * - `invalidateCache()` — evicts the cached list for an organisation
 */

import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';

import {environment} from '../environments/environment';
import {
  BrandDomain,
  CreateBrandDomain,
  UpdateBrandDomain,
} from '../models/BrandDomain';
import {ApiClient} from './api-client.service';
import {RequestCacheService} from './request-cache.service';

interface BrandDomainListResponse {
  readonly data: BrandDomain[];
}

interface BrandDomainResponse {
  readonly data: BrandDomain;
}

const domainsUrl = (orgId: string) =>
  `${environment.backend.user_host}/organizations/${orgId}/domains`;

@Injectable({
  providedIn: 'root',
})
export class BrandDomainService {
  private readonly apiClient = inject(ApiClient);
  private readonly cache = inject(RequestCacheService);

  /**
   * Returns the brand domains of an organisation, default entry first.
   *
   * @param organizationId - Organisation UUID.
   */
  load$(organizationId: string): Observable<BrandDomain[]> {
    return this.apiClient
      .getShared<BrandDomainListResponse>(domainsUrl(organizationId))
      .pipe(map((response) => response.data));
  }

  /**
   * Adds a brand domain. Validation errors are left to the caller (Pattern B).
   *
   * @param organizationId - Organisation UUID.
   * @param domain - Host, optional label/logo/default flag.
   */
  create$(
    organizationId: string,
    domain: CreateBrandDomain,
  ): Observable<BrandDomain> {
    return this.apiClient
      .post<BrandDomainResponse>(domainsUrl(organizationId), domain)
      .pipe(
        map((response) => response.data),
        tap(() => this.invalidateCache(organizationId)),
      );
  }

  /**
   * Updates label, logo or default flag of a brand domain.
   *
   * @param organizationId - Organisation UUID.
   * @param domainId - Brand domain UUID.
   * @param changes - Fields to update.
   */
  update$(
    organizationId: string,
    domainId: string,
    changes: UpdateBrandDomain,
  ): Observable<BrandDomain> {
    return this.apiClient
      .patch<BrandDomainResponse>(
        `${domainsUrl(organizationId)}/${domainId}`,
        changes,
      )
      .pipe(
        map((response) => response.data),
        tap(() => this.invalidateCache(organizationId)),
      );
  }

  /**
   * Removes a non-default brand domain.
   *
   * @param organizationId - Organisation UUID.
   * @param domainId - Brand domain UUID.
   */
  delete$(organizationId: string, domainId: string): Observable<void> {
    return this.apiClient
      .delete<void>(`${domainsUrl(organizationId)}/${domainId}`)
      .pipe(tap(() => this.invalidateCache(organizationId)));
  }

  /** Evicts the cached brand-domain list of an organisation. */
  invalidateCache(organizationId: string): void {
    this.cache.invalidate(domainsUrl(organizationId));
  }
}
